import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomerLayout from "../../../layouts/CustomerLayout";
import contactRequests from "../../../api/Customer/ContactRequests";
import GroupInfoSection from "../../../components/contacts/GroupInfoSection";
import SingleContactForm from "../../../components/contacts/SingleContactForm";
import BulkUploadSection from "../../../components/contacts/BulkUploadSection";
import ContactStats from "../../../components/contacts/ContactStats";
import ViewContacts from "../../../components/contacts/ViewContacts";
import { ArrowLeft, Settings, Plus, Upload, Eye } from "lucide-react";

const ContactGroupManagement = () => {
  const navigate = useNavigate();
  const { groupSlug } = useParams();

  // State management
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");
  const [refreshing, setRefreshing] = useState(false);

  // Load group data
  useEffect(() => {
    loadGroupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSlug]);

  const loadGroupData = async () => {
    try {
      setLoading(true);
      const response = await contactRequests.getContactGroupBySlug(groupSlug);
      if (response.success) {
        setGroup(response.data.data.data);
      } else {
        toast.error("Failed to load group data");
        navigate("/contacts");
      }
    } catch (error) {
      console.error("Error loading group:", error);
      toast.error("Error loading group data");
      navigate("/contacts");
    } finally {
      setLoading(false);
    }
  };

  const refreshGroup = async () => {
    setRefreshing(true);
    await loadGroupData();
    setRefreshing(false);
  };

  const handleGroupUpdate = async (updatedData) => {
    try {
      const response = await contactRequests.changeGroupName(
        group.contact_group_slug,
        updatedData.group_name
      );
      if (response.success) {
        toast.success("Group updated successfully!");
        await loadGroupData();
      } else {
        toast.error(response.error?.message || "Failed to update group");
      }
    } catch (error) {
      toast.error("Failed to update group");
    }
  };

  const handleSingleContactAdd = async (contactData) => {
    try {
      const response = await contactRequests.updateContact({
        ...contactData,
        group_name: group.group_name,
        contact_group_slug: groupSlug,
      });

      if (response.success) {
        toast.success("Contact added successfully!");
        await loadGroupData();
      } else {
        toast.error(response.error?.message || "Failed to add contact");
      }
    } catch (error) {
      toast.error("Error adding contact");
    }
  };

  const handleBulkUpload = async (file, groupName) => {
    try {
      const response = await contactRequests.updateBulkExcelFile(
        file,
        groupSlug
      );

      if (response.success) {
        toast.success("Contacts uploaded successfully! Processing...");
        await loadGroupData();
      } else {
        toast.error(response.error?.message || "Failed to upload contacts");
      }
    } catch (error) {
      toast.error("Error uploading contacts");
    }
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
            <span className="text-gray-600">Loading group...</span>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  if (!group) {
    return null;
  }

  const tabs = [
    {
      id: "info",
      label: "Group Info",
      icon: Settings,
      description: "Manage group details",
    },
    {
      id: "single",
      label: "Add Contact",
      icon: Plus,
      description: "Add individual contacts",
    },
    {
      id: "bulk",
      label: "Bulk Upload",
      icon: Upload,
      description: "Upload multiple contacts",
    },
    {
      id: "view",
      label: "View Contacts",
      icon: Eye,
      description: "View all contacts in this group",
    },
  ];

  return (
    <CustomerLayout>
      <div className="py-4 pt-16 min-h-screen bg-gray-50 sm:py-6 sm:pt-20">
        <div className="px-3 mx-auto max-w-8xl sm:px-4 md:px-6 lg:px-8">
          {/* Header */}
          <div className="p-4 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
            <div className="flex gap-3 items-center mb-4 sm:gap-4 sm:mb-6">
              <button
                onClick={() => navigate("/contacts")}
                className="flex-shrink-0 p-2 text-gray-600 rounded-lg transition-colors duration-200 hover:text-gray-900 hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate sm:text-2xl lg:text-3xl">
                  Manage {group.group_name}
                </h1>
                <p className="mt-1 text-sm text-gray-600 sm:text-base">
                  Add contacts and manage your group settings
                </p>
              </div>
              <button
                onClick={refreshGroup}
                disabled={refreshing}
                className="flex-shrink-0 p-2 text-gray-600 rounded-lg transition-colors duration-200 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50"
              >
                <div className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </div>
              </button>
            </div>

            {/* Contact Stats */}
            <ContactStats group={group} />
          </div>

          {/* Tab Navigation */}
          <div className="p-4 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex gap-3 items-center p-4 rounded-xl transition-all duration-200 ${
                      isActive
                        ? "text-white bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg"
                        : "text-gray-600 bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        isActive ? "text-white" : "text-gray-500"
                      }`}
                    />
                    <div className="text-left">
                      <div className="font-medium">{tab.label}</div>
                      <div
                        className={`text-xs ${
                          isActive ? "text-blue-100" : "text-gray-500"
                        }`}
                      >
                        {tab.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === "info" && (
              <GroupInfoSection
                group={group}
                onUpdate={handleGroupUpdate}
                onRefresh={refreshGroup}
              />
            )}

            {activeTab === "single" && (
              <SingleContactForm
                groupName={group.group_name}
                onAddContact={handleSingleContactAdd}
                onRefresh={refreshGroup}
              />
            )}

            {activeTab === "bulk" && (
              <BulkUploadSection
                groupName={group.group_name}
                onUpload={handleBulkUpload}
                onRefresh={refreshGroup}
              />
            )}

            {activeTab === "view" && (
              <ViewContacts
                groupSlug={groupSlug}
                groupName={group.group_name}
                onRefresh={refreshGroup}
              />
            )}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default ContactGroupManagement;